import { API_VIDEO_PREFIX, getValidAccessTokenFromStorage } from "@weco/suite-client";
import type { CallQualityReport } from "../lib/callQuality";
import type { CallActivityBody, CallActivityResult } from "../lib/callActivity";
import { videoApi } from "./client";
import { noteCallDiagnostics } from "../store/callDiagnosticsStore";
import type {
  Room,
  RoomsResponse,
  CreateRoomRequest,
  LiveCallMap,
  LobbyResponse,
  LobbyParticipant,
  DirectCallInvite,
  GroupCallResponse,
  RingIntoRoomResponse,
} from "./types/video";
import type { ListParams } from "./types/common";

export interface ChatMessage {
  seq: number;
  timestamp: string;
  sender_name: string;
  sender_id: string;
  text: string;
}

export const videoService = {
  getRooms: async (params?: ListParams): Promise<RoomsResponse> => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return videoApi.get(`/rooms/${query ? `?${query}` : ""}`);
  },

  getRoom: async (
    roomId: string,
    opts?: { username?: string; skipAuth?: boolean; sources?: string[] },
  ): Promise<Room> => {
    // `?username=` is honored by RoomSerializer.to_representation and embedded
    // into the minted LiveKit JWT's `name` claim, so it becomes the
    // participant's initial display name on connect. Without it the backend
    // falls back to `str(user)` (the user's email), which surfaces in tiles
    // and chat as "admin@weco.local" instead of the PreJoin-entered name.
    //
    // `skipAuth: true` is required for anonymous guests on Room.allow_guests
    // rooms — without it, SuiteApiClient.authHeaders() throws a synthetic 401
    // before the fetch ever leaves the browser, and the backend's guest-token
    // path is unreachable from the frontend.
    // `?sources=` names the track kinds the minted token may publish. The
    // backend default is camera + microphone, so a caller that wants to share a
    // screen has to say so — LiveKit refuses the track otherwise, however the
    // button looks.
    const params = new URLSearchParams();
    if (opts?.username) params.set("username", opts.username);
    if (opts?.sources?.length) params.set("sources", opts.sources.join(","));
    const qs = params.toString() ? `?${params.toString()}` : "";
    return noteCallDiagnostics(await videoApi.get<Room>(`/rooms/${roomId}/${qs}`, { skipAuth: opts?.skipAuth }));
  },

  createRoom: async (request: CreateRoomRequest): Promise<Room> => {
    return videoApi.post("/rooms/", request);
  },

  /**
   * Chat conversations with a call in progress, for the caller's organization.
   *
   * `conversationIds` narrows the response, but it is hygiene rather than
   * authorization — the endpoint is org-scoped, since video cannot see chat
   * membership. Callers that need every channel at once (the sidebar) omit it
   * so one shared poll serves the whole UI.
   */
  getLiveCalls: async (conversationIds?: string[]): Promise<LiveCallMap> => {
    const query = conversationIds?.length
      ? `?conversation_ids=${encodeURIComponent(conversationIds.join(","))}`
      : "";
    return videoApi.get(`/rooms/live/${query}`);
  },

  deleteRoom: async (roomId: string): Promise<void> => {
    return videoApi.delete(`/rooms/${roomId}/`);
  },

  acquireScreenShare: async (roomId: string, token: string): Promise<{ ok: true }> => {
    return videoApi.post(`/rooms/${roomId}/screen-share/acquire/`, { token }, { skipAuth: true });
  },

  releaseScreenShare: async (roomId: string, token: string): Promise<{ ok: true }> => {
    return videoApi.post(`/rooms/${roomId}/screen-share/release/`, { token }, { skipAuth: true });
  },

  inviteToRoom: async (
    roomId: string,
    body: { emails: string[]; message?: string },
  ): Promise<{
    sent: number;
    errors: { email: string; reason: string }[];
  }> => {
    return videoApi.post(`/rooms/${roomId}/invite/`, body);
  },

  updateRoom: async (
    roomId: string,
    patch: Partial<{
      allow_guests: boolean;
      waiting_room_enabled: boolean;
      admit_policy: "owners" | "admins" | "participants";
      name: string;
    }>,
  ): Promise<Room> => {
    return videoApi.patch(`/rooms/${roomId}/`, patch);
  },

  /** POST /rooms/{id}/request-entry/ — guest or logged-in participant requests
   *  lobby entry. Always `skipAuth` (SuiteApiClient.authHeaders() throws when
   *  no token exists, which would break real anonymous guests, and a 401 here
   *  must not trigger the client's session-expired redirect). But a logged-in
   *  caller's identity still needs to reach the backend — otherwise their
   *  admitted LiveKit token gets minted as an anonymous participant_id instead
   *  of their real `sub`, and every "participant" permission check downstream
   *  (e.g. admit_policy="participants") silently fails for them. So attach
   *  the bearer token by hand, only when one is actually present. */
  requestEntry: async (roomId: string, username: string): Promise<LobbyResponse> => {
    const token = await getValidAccessTokenFromStorage();
    return noteCallDiagnostics(
      await videoApi.post<LobbyResponse>(
        `/rooms/${roomId}/request-entry/`,
        { username },
        {
          skipAuth: true,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      ),
    );
  },

  /** GET /rooms/{id}/waiting-participants/ — list guests in WAITING state.
   *  Requires a valid LiveKit room token (sent as X-LiveKit-Token header).
   *  `skipAuth`: the endpoint authenticates by LiveKit token only, and an
   *  anonymous link guest has no WeCo session — without it the client throws
   *  "No access token available" before the request is even sent (#1850). */
  getWaitingParticipants: async (
    roomId: string,
    lkToken: string,
  ): Promise<{ participants: LobbyParticipant[] }> => {
    return videoApi.get(`/rooms/${roomId}/waiting-participants/`, {
      skipAuth: true,
      headers: { "X-LiveKit-Token": lkToken },
    });
  },

  /** POST /rooms/{id}/enter/ — admit or deny a waiting participant.
   *  Requires a valid LiveKit room token when admit_policy="participants".
   *  `skipAuth` for the same reason as getWaitingParticipants. */
  admitParticipant: async (
    roomId: string,
    participantId: string,
    allow: boolean,
    lkToken: string,
  ): Promise<void> => {
    return videoApi.post(
      `/rooms/${roomId}/enter/`,
      { participant_id: participantId, allow_entry: allow },
      { skipAuth: true, headers: { "X-LiveKit-Token": lkToken } },
    );
  },

  /** POST /rooms/{id}/requeue/ — put an admitted participant who never
   *  connected back in the waiting list (#1916). Allowed for the owner, or
   *  for whoever admitted them under admit_policy="participants". */
  requeueParticipant: async (
    roomId: string,
    participantId: string,
    lkToken: string,
  ): Promise<void> => {
    return videoApi.post(
      `/rooms/${roomId}/requeue/`,
      { participant_id: participantId },
      { skipAuth: true, headers: { "X-LiveKit-Token": lkToken } },
    );
  },

  getChatHistory: async (roomId: string, token: string): Promise<ChatMessage[]> => {
    return videoApi.get(`/rooms/${roomId}/chat/`, {
      skipAuth: true,
      headers: { "X-LiveKit-Token": token },
    });
  },

  /** POST /rooms/<id>/call-quality/ — this participant's connection quality
   *  for the super admin's call report. `keepalive` lets the final report
   *  go out while the page is closing. Best effort: never throws. */
  reportCallQuality: async (
    roomId: string,
    token: string,
    report: CallQualityReport,
    { keepalive = false }: { keepalive?: boolean } = {},
  ): Promise<void> => {
    try {
      await fetch(`${API_VIDEO_PREFIX}/rooms/${roomId}/call-quality/`, {
        method: "POST",
        keepalive,
        headers: { "Content-Type": "application/json", "X-LiveKit-Token": token },
        body: JSON.stringify(report),
      });
    } catch {
      // A lost report is a gap in an admin page, not the user's problem.
    }
  },

  /** POST /rooms/<id>/call-activity/ — the call recorder's batch of events
   *  (#2223 J1), authenticated exactly like call-quality. `delivered` is false
   *  only when the batch may be sent again (network failure, 5xx); a 4xx is a
   *  refusal that resending cannot fix. Never throws. */
  reportCallActivity: async (
    roomId: string,
    token: string,
    body: CallActivityBody,
    { keepalive = false }: { keepalive?: boolean } = {},
  ): Promise<CallActivityResult> => {
    try {
      const res = await fetch(`${API_VIDEO_PREFIX}/rooms/${roomId}/call-activity/`, {
        method: "POST",
        keepalive,
        headers: { "Content-Type": "application/json", "X-LiveKit-Token": token },
        body: JSON.stringify(body),
      });
      if (res.status >= 500) return { delivered: false, truncated: false };
      if (res.status !== 202) return { delivered: true, truncated: false };
      const json = (await res.json().catch(() => null)) as { truncated?: unknown } | null;
      return { delivered: true, truncated: json?.truncated === true };
    } catch {
      return { delivered: false, truncated: false };
    }
  },

  sendChatMessage: async (roomId: string, text: string, token: string): Promise<ChatMessage> => {
    return videoApi.post(`/rooms/${roomId}/chat/`, { text }, {
      skipAuth: true,
      headers: { "X-LiveKit-Token": token },
    });
  },

  /** POST /direct-calls/ — ring a specific person. Creates an ad-hoc room +
   *  a RINGING invite; the response includes a LiveKit token for the caller.
   *
   *  `conversationId` names the DM the call came from, so an unanswered ring
   *  can be written back into that DM as a system message (#1386). Optional:
   *  a call placed from anywhere but a DM has no conversation to log into. */
  placeDirectCall: async (
    calleeSub: string,
    conversationId?: string,
  ): Promise<DirectCallInvite> => {
    return noteCallDiagnostics(
      await videoApi.post<DirectCallInvite>("/direct-calls/", {
        callee_sub: calleeSub,
        ...(conversationId ? { conversation_id: conversationId } : {}),
      }),
    );
  },

  /** GET /direct-calls/pending/ — RINGING invites addressed to the current
   *  user. Polled by the incoming-call banner. */
  getPendingDirectCalls: async (): Promise<DirectCallInvite[]> => {
    return videoApi.get("/direct-calls/pending/");
  },

  /** GET /direct-calls/{id}/ — polled by the caller's ringing screen to
   *  detect the callee's accept/decline, or a timeout. */
  getDirectCall: async (id: string): Promise<DirectCallInvite> => {
    return videoApi.get(`/direct-calls/${id}/`);
  },

  /** POST /direct-calls/{id}/respond/ — callee accepts or declines. On
   *  accept, the response includes a LiveKit token for the callee. */
  respondToDirectCall: async (id: string, accept: boolean): Promise<DirectCallInvite> => {
    return noteCallDiagnostics(await videoApi.post<DirectCallInvite>(`/direct-calls/${id}/respond/`, { accept }));
  },

  /** POST /direct-calls/{id}/cancel/ — caller gives up while still ringing. */
  cancelDirectCall: async (id: string): Promise<DirectCallInvite> => {
    return videoApi.post(`/direct-calls/${id}/cancel/`, {});
  },

  /** POST /direct-calls/group/ — ring every other member of a group DM.
   *
   *  Takes the conversation, not a list of callees: video re-derives the
   *  membership from chat using the caller's own token, so a client cannot
   *  widen who gets rung. Creates or reuses the room bound to that
   *  conversation, so two people pressing call at once land in one call. */
  startGroupCall: async (conversationId: string): Promise<GroupCallResponse> => {
    return noteCallDiagnostics(await videoApi.post<GroupCallResponse>("/direct-calls/group/", { conversation_id: conversationId }));
  },

  /** POST /direct-calls/group/cancel/ — the caller of a group ring left before
   *  anyone answered, so everyone still ringing stops.
   *
   *  Safe to fire on *any* deliberate leave: the server only ever touches
   *  invites this user placed, and no-ops once someone has joined. That's what
   *  lets the leave handler call it without tracking whether this was a group
   *  call in the first place. */
  cancelGroupRing: async (roomId: string): Promise<{ canceled: number }> => {
    return videoApi.post("/direct-calls/group/cancel/", { room_id: roomId });
  },

  /** POST /direct-calls/ring/ — ring colleagues picked from the org directory
   *  into a call that is **already in progress** (GH-1384).
   *
   *  The third ring shape, alongside placeDirectCall (one callee, always a new
   *  room) and startGroupCall (a chat conversation's members): this one takes
   *  an existing room plus an explicit callee list, because the whole point is
   *  ringing colleagues the caller may share no conversation with.
   *
   *  Errors worth handling at the call site: 400 (someone in the list is not
   *  in the caller's organisation — the *whole* request is refused, nobody is
   *  rung), 403 (the caller is not part of this call), 409 (everyone picked is
   *  already here or already ringing), 503 (the directory is unreachable, so
   *  the server fails closed rather than ringing unverified subs). */
  ringIntoRoom: async (
    roomId: string,
    calleeSubs: string[],
  ): Promise<RingIntoRoomResponse> => {
    return videoApi.post("/direct-calls/ring/", {
      room_id: roomId,
      callee_subs: calleeSubs,
    });
  },

  /** POST /rooms/{id}/participants/{identity}/role — promote/demote a live
   *  participant's in-call role (#215). Admin-only server-side
   *  (`room_roles.require_admin`); a non-admin caller gets a 403.
   *
   *  `makePermanent` additionally writes a durable `ResourceAccess` row for
   *  the target (no-op for guests, who have no durable account — the
   *  response's `note` field explains that case). */
  setParticipantRole: async (
    roomId: string,
    identity: string,
    role: "admin" | "user" | "guest",
    makePermanent?: boolean,
  ): Promise<{ identity: string; role: string; make_permanent: boolean; note: string | null }> => {
    return videoApi.post(`/rooms/${roomId}/participants/${encodeURIComponent(identity)}/role`, {
      role,
      ...(makePermanent ? { make_permanent: true } : {}),
    });
  },
};
